import React from 'react';
import { BarChart3, Globe, LayoutDashboard, Monitor, Smartphone, TrendingUp } from 'lucide-react';

/**
 * Mockups flutuantes (HTML/CSS) para o hero de Desenvolvimento Web.
 */
export function WebDevHeroMockups({ mock }) {
  return (
    <div className="relative mx-auto w-full max-w-xl lg:max-w-none lg:mx-0 min-h-[400px] sm:min-h-[460px] lg:min-h-[520px] overflow-x-hidden">
      <div
        className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-sky-500/[0.08] via-transparent to-indigo-500/[0.07] blur-2xl pointer-events-none animate-pulse"
        style={{ animationDuration: '4s' }}
        aria-hidden
      />

      {/* Dashboard */}
      <div className="absolute left-0 top-6 sm:top-10 w-[min(100%,270px)] rounded-2xl border border-white/[0.12] bg-slate-950/85 backdrop-blur-xl shadow-[0_24px_80px_-24px_rgba(56,189,248,0.25)] p-4 z-20 rotate-[-2deg] hover:rotate-0 transition-transform duration-500">
        <div className="flex items-center gap-2 mb-3">
          <LayoutDashboard className="w-4 h-4 text-sky-400" aria-hidden />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-white/45">{mock.dashboardLabel}</span>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[72, 48, 91].map((h, i) => (
            <div key={i} className="rounded-lg bg-gradient-to-t from-sky-500/30 to-sky-400/10 border border-sky-500/20 flex items-end p-1.5" style={{ height: `${h}px` }}>
              <div className="w-full h-2 rounded-full bg-sky-400/40" />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-white/[0.08]">
          <span className="text-[10px] text-white/40">{mock.metric1}</span>
          <span className="text-sm font-black text-sky-400 tabular-nums">{mock.metric3}</span>
        </div>
      </div>

      {/* Website desktop */}
      <div className="absolute right-0 top-0 w-[min(100%,260px)] rounded-2xl border border-white/[0.12] bg-slate-950/80 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(99,102,241,0.3)] p-3 z-10 rotate-[2deg] sm:right-2 lg:right-6 hover:rotate-0 transition-transform duration-500">
        <div className="flex items-center gap-1.5 mb-2.5 px-1">
          <div className="w-2 h-2 rounded-full bg-red-400/80" />
          <div className="w-2 h-2 rounded-full bg-amber-400/80" />
          <div className="w-2 h-2 rounded-full bg-emerald-400/80" />
          <Monitor className="w-3.5 h-3.5 text-indigo-400 ml-auto" aria-hidden />
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-gradient-to-br from-indigo-500/10 to-slate-900/80 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-3.5 h-3.5 text-indigo-300" aria-hidden />
            <span className="text-[10px] font-semibold text-white/70">{mock.websiteLabel}</span>
          </div>
          <div className="h-16 rounded-lg bg-white/[0.04] border border-white/[0.06] mb-2" />
          <div className="space-y-1.5">
            <div className="h-2 w-full rounded-full bg-white/10" />
            <div className="h-2 w-4/5 rounded-full bg-white/[0.06]" />
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="absolute left-8 sm:left-16 bottom-32 sm:bottom-36 w-[120px] rounded-[1.25rem] border border-white/[0.15] bg-slate-950/90 backdrop-blur-xl shadow-[0_16px_50px_-16px_rgba(0,0,0,0.8)] p-2 z-30 rotate-[4deg] hover:rotate-0 transition-transform duration-500">
        <div className="mx-auto w-8 h-1 rounded-full bg-white/20 mb-2" />
        <div className="rounded-xl bg-gradient-to-b from-sky-500/15 to-slate-900/90 p-2 border border-white/[0.06]">
          <Smartphone className="w-3.5 h-3.5 text-sky-300 mx-auto mb-1.5" aria-hidden />
          <span className="block text-[8px] text-center font-semibold text-white/60 mb-2">{mock.mobileLabel}</span>
          <div className="space-y-1">
            <div className="h-1.5 w-full rounded-full bg-white/10" />
            <div className="h-1.5 w-3/4 rounded-full bg-white/[0.06]" />
            <div className="h-8 rounded-md bg-sky-500/20 border border-sky-500/25 mt-1.5" />
          </div>
        </div>
      </div>

      {/* Analytics */}
      <div className="absolute right-8 sm:right-14 bottom-20 sm:bottom-24 w-[min(100%,200px)] rounded-xl border border-emerald-500/25 bg-emerald-950/40 backdrop-blur-md px-3 py-2.5 z-40 shadow-lg">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-emerald-400 shrink-0" aria-hidden />
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-emerald-200/95">{mock.analyticsLabel}</p>
            <p className="text-[9px] text-emerald-200/55 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" aria-hidden />
              {mock.metric2} · {mock.live}
            </p>
          </div>
        </div>
      </div>

      {/* Admin / SaaS center */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-4 sm:bottom-8 w-[min(100%,290px)] rounded-2xl border border-amber-500/20 bg-gradient-to-br from-slate-950/95 to-slate-900/85 backdrop-blur-xl shadow-[0_28px_90px_-28px_rgba(251,191,36,0.25)] p-4 z-25">
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-200/80">{mock.saasLabel}</span>
          <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[9px] font-bold text-amber-300">{mock.adminLabel}</span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {['#', '#', '#', '#'].map((_, i) => (
            <div key={i} className="aspect-square rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-600/5 border border-amber-500/15" />
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-white/[0.08] flex justify-between items-center">
          <div className="h-2 w-20 rounded-full bg-white/10" />
          <div className="h-6 w-16 rounded-lg bg-gradient-to-r from-amber-500/40 to-amber-600/30 border border-amber-500/30" />
        </div>
      </div>
    </div>
  );
}
